import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import moment from "moment";
import "moment/locale/pt-br";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { useCashRegister } from "../cash-register-context";
import { CashRegisterSectionTabs } from "../components/cash-register-section-tabs";
import { CashRegisterReportHero } from "../components/cash-register-report-hero";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { CashEntryDialog } from "../components/cash-entry-dialog";
import { CashWithdrawalDialog } from "../components/cash-withdrawal-dialog";
import { CashMovementsLogView } from "../components/cash-movements-log-view";
import { SellPaymentMethod } from "../../sell/sell.entity";

export function CashRegisterPage() {
  application.useHasPermission(Permission.CASH_REGISTER_MANAGEMENT);
  const theme = useTheme();

  const moduleAccess = useModuleAccess();

  const {
    loading,
    session,
    sessionExpired,
    refresh,
    openSession,
    closeSession,
    movements,
    sells,
    disabledBySettings,
  } = useCashRegister();

  const [openDialog, setOpenDialog] = useState(false);
  const [openingInput, setOpeningInput] = useState("");
  const [closeDialog, setCloseDialog] = useState(false);
  const [closingInput, setClosingInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [withdrawalDialogOpen, setWithdrawalDialogOpen] = useState(false);

  const clearErrorOnOpen = useCallback(() => setError(null), []);

  useEffect(() => {
    if (openDialog || closeDialog) clearErrorOnOpen();
  }, [openDialog, closeDialog, clearErrorOnOpen]);

  const parseMoney = (raw: string) => {
    const t = raw.replace(",", ".").trim();
    if (t === "") return null;
    const n = parseFloat(t);
    if (Number.isNaN(n) || n < 0) return null;
    return n;
  };

  const handleOpenSession = async () => {
    const v = parseMoney(openingInput);
    if (v === null) {
      setError("Informe um valor inicial válido.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await openSession(v);
      setOpenDialog(false);
      setOpeningInput("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível abrir o caixa.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSession = async () => {
    const v = parseMoney(closingInput);
    if (v === null) {
      setError("Informe o valor contado no caixa.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await closeSession(v);
      setCloseDialog(false);
      setClosingInput("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível fechar o caixa.");
    } finally {
      setSubmitting(false);
    }
  };

  const openToday =
    session &&
    session.getStatus() === "open" &&
    !sessionExpired;

  const expiredStillOpen =
    session &&
    session.getStatus() === "open" &&
    sessionExpired;

  if (moduleAccess.loading) {
    return (
      <Shell title="Caixa">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("cash_register")) {
    return (
      <Shell title="Caixa">
        <PageHeader title="Fluxo de caixa" description="Módulo não contratado." />
        <ModuleLockedView
          moduleName="Controle de Caixa"
          groupId="cash_register"
          details="Ative o módulo para usar abertura, fechamento e relatórios por sessão."
          features={["Abertura e fechamento de sessão", "Relatório de receitas por turno", "Controle de sangria e suprimentos"]}
        />
      </Shell>
    );
  }

  if (disabledBySettings) {
    return (
      <Shell title="Caixa">
        <PageHeader
          title="Fluxo de caixa desativado"
          description="O controle de caixa está desativado nas configurações. Você pode reativar a qualquer momento."
        />
        <Button
          variant="outlined"
          component={Link}
          to="/configuracoes"
          sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
        >
          Ir para Configurações
        </Button>
      </Shell>
    );
  }

  return (
    <Shell title="Caixa">
      {submitting ? <LoadingOverlay subtle message="Salvando…" /> : null}

      <PageHeader
        title="Operação de caixa"
        description="Abertura com dinheiro em espécie, fechamento com conferência e histórico detalhado na aba de relatórios."
      />

      <CashRegisterSectionTabs />

      <Stack spacing={3}>
        <CashRegisterReportHero
          title="Situação atual"
          chips={
            loading ? (
              <Chip size="small" label="Sincronizando…" sx={{ fontWeight: 600 }} />
            ) : expiredStillOpen ? (
              <Chip size="small" color="warning" label="Fechamento obrigatório" sx={{ fontWeight: 700 }} />
            ) : openToday ? (
              <Chip
                size="small"
                label={`Aberto · ${moment(session!.getOpenedAt()).format("HH:mm")}`}
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                }}
              />
            ) : (
              <Chip size="small" variant="outlined" label="Nenhum caixa aberto" sx={{ fontWeight: 600 }} />
            )
          }
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            {loading ? (
              <LoadingOverlay message="Carregando sessão…" />
            ) : expiredStillOpen ? (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.warning.main, 0.45),
                  background: alpha(theme.palette.warning.main, 0.06),
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                    Caixa do dia anterior
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    É obrigatório fechar este caixa antes de continuar a operar.
                  </Alert>
                  <Button
                    variant="contained"
                    color="warning"
                    component={Link}
                    to="/caixa/fechar"
                    fullWidth
                    sx={{ py: 1.25, fontWeight: 800, borderRadius: 2 }}
                  >
                    Ir para fechamento obrigatório
                  </Button>
                </CardContent>
              </Card>
            ) : openToday ? (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Aberto em
                  </Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                    {moment(session!.getOpenedAt()).format("LLL")}
                  </Typography>

                  {/* Saldo atual */}
                  {(() => {
                    const totalEntries = movements.filter((m) => m.getType() === "entry").reduce((s, m) => s + m.getAmount(), 0);
                    const totalWithdrawals = movements.filter((m) => m.getType() === "withdrawal").reduce((s, m) => s + m.getAmount(), 0);
                    const totalCashSales = sells
                      .filter((s) => s.getPaymentMethod() === SellPaymentMethod.MONEY && s.getCompletedAt() !== null)
                      .reduce((acc, s) => acc + s.getSellTotal(), 0);
                    const balance = session!.getOpeningCash() + totalEntries - totalWithdrawals + totalCashSales;
                    return (
                      <Box
                        sx={{
                          mb: 2.5,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          border: "1px solid",
                          borderColor: alpha(theme.palette.primary.main, 0.15),
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Saldo atual em caixa
                        </Typography>
                        <Typography variant="h5" fontWeight={800} color="primary.dark">
                          {balance.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Abertura + vendas dinheiro + entradas − retiradas
                        </Typography>
                      </Box>
                    );
                  })()}

                  <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => setEntryDialogOpen(true)}
                      sx={{ flex: 1, borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                    >
                      Entrada
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<RemoveCircleOutlineIcon />}
                      onClick={() => setWithdrawalDialogOpen(true)}
                      sx={{ flex: 1, borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                    >
                      Retirada
                    </Button>
                  </Stack>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => setCloseDialog(true)}
                    sx={{ borderRadius: 2, py: 1.25, fontWeight: 800 }}
                  >
                    Fechar caixa
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                    Pronto para abrir
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Informe o valor em espécie que está no caixa neste momento.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => setOpenDialog(true)}
                    sx={{ borderRadius: 2, py: 1.25, fontWeight: 800 }}
                  >
                    Abrir caixa
                  </Button>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 3,
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.15),
                background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
                  theme.palette.background.paper,
                  1
                )} 100%)`,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <AssessmentOutlinedIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={800}>
                    Relatórios
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Veja sessões por dia, totais e conferência de dinheiro — no mesmo estilo das
                  estatísticas de vendas.
                </Typography>
                <Button
                  component={Link}
                  to="/caixa/relatorio"
                  variant="outlined"
                  fullWidth
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                >
                  Abrir relatórios por dia
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {openToday && (
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                Movimentações desta sessão
              </Typography>
              <CashMovementsLogView
                openingCash={session!.getOpeningCash()}
                movements={movements}
                sells={sells}
                detailLink={`/caixa/sessao/${session!.id}`}
              />
            </CardContent>
          </Card>
        )}
      </Stack>

      <CashEntryDialog
        open={entryDialogOpen}
        onClose={() => {
          setEntryDialogOpen(false);
          void refresh();
        }}
      />

      <CashWithdrawalDialog
        open={withdrawalDialogOpen}
        onClose={() => {
          setWithdrawalDialogOpen(false);
          void refresh();
        }}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Abrir caixa</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe quanto há em dinheiro em espécie no caixa neste momento.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Valor em dinheiro (R$)"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={openingInput}
            onChange={(e) => setOpeningInput(e.target.value)}
          />
          {error ? (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleOpenSession()} sx={{ fontWeight: 800 }}>
            Abrir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={closeDialog}
        onClose={() => setCloseDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Fechar caixa</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe o valor em dinheiro contado no caixa para gerar a conferência (mesma lógica do
            relatório de vendas).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Valor contado (R$)"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={closingInput}
            onChange={(e) => setClosingInput(e.target.value)}
          />
          {error ? (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCloseDialog(false)} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleCloseSession()} sx={{ fontWeight: 800 }}>
            Fechar e salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Shell>
  );
}
